import React, { useState, useMemo } from 'react';
import {
  Table,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Tooltip,
  Typography,
  message,
  Modal,
  Popconfirm,
  Segmented,
  Switch,
  Dropdown,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  GiftOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  RocketOutlined,
  UpOutlined,
  AppstoreOutlined,
  TableOutlined,
  ImportOutlined,
  MoreOutlined,
  FilterOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import CampaignRequestorLineItem from './CampaignRequestorLineItem';
import LineItemImportModal from './components/LineItemImportModal';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { isPercentageModel } from '../../../core/utils/pricing/cpmHelper';
import { normalizeLineItemPercentages, createFlightSchedulesWithBudget } from './utils/lineItemImport.utils';
import { useTimezone } from '../../../core/hooks/useTimezone';
import {
  toDatePickerDisplay,
  datePickerToStartOfDayUtc,
  datePickerToEndOfDayUtc,
} from './utils/dateNormalization';

const { Text } = Typography;

// Safe DatePicker wrapper that ensures moment objects and normalizes dates to UTC
const SafeDatePicker = ({ value, onChange, normalizeToEndOfDay = false, ...props }) => {
  const { timezone } = useTimezone();

  // Convert stored UTC value to user's timezone for display
  const safeValue = toDatePickerDisplay(value, timezone);

  const handleChange = (date) => {
    if (!date) {
      onChange && onChange(date);
      return;
    }

    // Normalize through the timezone-aware pipeline
    const normalizedDate = normalizeToEndOfDay
      ? datePickerToEndOfDayUtc(date, timezone)
      : datePickerToStartOfDayUtc(date, timezone);

    onChange && onChange(normalizedDate);
  };

  return <DatePicker {...props} value={safeValue} onChange={handleChange} format="MM/DD/YYYY" />;
};

/**
 * Validates if a line item has all required fields for delivery
 * @param {Object} lineItem - The line item object to validate
 * @param {boolean} hideCpmFields - Whether CPM fields should be hidden (Percentage pricing model)
 * @returns {Object} { isValid: boolean, missingFields: string[] }
 */
const validateLineItemForDelivery = (lineItem, hideCpmFields) => {
  const missingFields = [];

  if (!lineItem.name || lineItem.name.trim() === '') {
    missingFields.push('Line Item Name');
  }
  if (!lineItem.startDate) {
    missingFields.push('Start Date');
  }
  if (!lineItem.endDate) {
    missingFields.push('End Date');
  }
  const budget = parseFloat(lineItem.budgetTotal);
  if (!budget || budget <= 0) {
    missingFields.push('Budget');
  }
  if (!lineItem.targetingType) {
    missingFields.push('Targeting Type');
  }
  if (!hideCpmFields) {
    const cpm = parseFloat(lineItem.cpm);
    if (!cpm || cpm < 1) {
      missingFields.push('CPM');
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

/**
 * CampaignRequestorLineItemTable Component
 *
 * Displays line items in a table format with inline editing capabilities.
 * Permission-gated for Ad Ops users (isTrafficker/isProcessor) on non-DRAFT campaigns.
 */
const CampaignRequestorLineItemTable = ({
  campaignIndex,
  formValues,
  derivedState,
  helpers,
  lineItemFields,
  addLineItem,
  removeLineItem,
  currentCampaignOrder,
  isAdvancedMode,
  hasTraffickerOrProcessorPermission,
  isNotDraftStatus,
  campaignDsp,
  campaignValueAdd,
  form,
  viewMode = 'table',
  onViewModeChange,
  showOnlyDeliveryItems = false,
  onDeliveryFilterChange,
  onSyncLineItems,
  isSyncing = false,
  onSaveLineItem = null,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [addCount, setAddCount] = useState(1);
  const [isAddingLineItems, setIsAddingLineItems] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);

  // Get context for pricing model
  const { selectedAdvertiser, currentCampaignOrder: contextCampaignOrder } = useCampaignRequestorContext();
  const { timezone } = useTimezone();

  // Check if pricing model is Percentage (hide CPM fields)
  const hideCpmFields = useMemo(() => {
    const campaignOrder = currentCampaignOrder || contextCampaignOrder;
    if (campaignOrder?.pricingModel) {
      return campaignOrder.pricingModel === 'Percentage';
    }
    return isPercentageModel(selectedAdvertiser);
  }, [currentCampaignOrder, contextCampaignOrder, selectedAdvertiser]);

  // Get line items from the current campaign
  const lineItems = formValues.campaigns?.[campaignIndex]?.lineItems || [];

  // Build table data source (filtered if Delivery Only is enabled)
  const dataSource = lineItems
    .map((lineItem, index) => ({
      key: index,
      ...lineItem,
      index,
    }))
    .filter((item) => !showOnlyDeliveryItems || item.markedForDelivery);

  // Toggle row expansion
  const toggleExpand = (index) => {
    setExpandedRowKeys(prev =>
      prev.includes(index) ? prev.filter(k => k !== index) : [...prev, index]
    );
  };

  // Helper function to prepare a cloned line item
  const prepareClonedLineItem = (sourceItem) => {
    let newLineItem = {
      ...JSON.parse(JSON.stringify(sourceItem)),
      impressions: 0,
    };

    // Remove $ suffix from line item name if it exists before adding (Copy)
    const baseLineItemName = newLineItem.name ? newLineItem.name.replace(/-\$\s*$/, '').trim() : '';
    const inheritedValueAdd = campaignValueAdd || false;
    newLineItem.name = inheritedValueAdd ? `${baseLineItemName} (Copy)-$` : `${baseLineItemName} (Copy)`;
    delete newLineItem.id;
    delete newLineItem.isOrigin;
    newLineItem.valueAdd = inheritedValueAdd;
    delete newLineItem.markedForDelivery;

    // Clean up nested IDs
    if (newLineItem.adGroup && newLineItem.adGroup.id) {
      delete newLineItem.adGroup.id;
      if (newLineItem.adGroup.adGroupCreatives && newLineItem.adGroup.adGroupCreatives.length) {
        newLineItem.adGroup.adGroupCreatives = newLineItem.adGroup.adGroupCreatives.map(
          (adGroupCreative) => {
            delete adGroupCreative.id;
            if (adGroupCreative.medias && adGroupCreative.medias.length) {
              adGroupCreative.medias = adGroupCreative.medias.map((media) => {
                delete media.id;
                return media;
              });
            }
            return adGroupCreative;
          }
        );
      }
    }

    if (newLineItem.firstPartyDataAudiences && newLineItem.firstPartyDataAudiences.length) {
      newLineItem.firstPartyDataAudiences = newLineItem.firstPartyDataAudiences.map((audience) => {
        delete audience.id;
        return audience;
      });
    }

    if (newLineItem.targetingRegions && newLineItem.targetingRegions.length) {
      newLineItem.targetingRegions = newLineItem.targetingRegions.map((region) => {
        delete region.id;
        return region;
      });
    }

    if (newLineItem.targetingLocations && newLineItem.targetingLocations.length) {
      newLineItem.targetingLocations = newLineItem.targetingLocations.map((location) => {
        delete location.id;
        return location;
      });
    }

    newLineItem.flightSchedule = [];
    delete newLineItem.flightSchedules;

    // Convert dates to moment objects
    newLineItem = {
      ...newLineItem,
      startDate: newLineItem.startDate ? moment(newLineItem.startDate) : null,
      endDate: newLineItem.endDate ? moment(newLineItem.endDate) : null,
      flightSchedule: newLineItem.flightSchedule
        ? newLineItem.flightSchedule.map((flight) => ({
            ...flight,
            startDate: flight.startDate ? moment(flight.startDate) : null,
            endDate: flight.endDate ? moment(flight.endDate) : null,
          }))
        : [],
      adGroup:
        newLineItem.adGroup && newLineItem.adGroup.adGroupCreatives
          ? {
              ...newLineItem.adGroup,
              adGroupCreatives: newLineItem.adGroup.adGroupCreatives.map((adGroupCreative) => ({
                ...adGroupCreative,
                startDate: adGroupCreative.startDate ? moment(adGroupCreative.startDate) : null,
                endDate: adGroupCreative.endDate ? moment(adGroupCreative.endDate) : null,
              })),
            }
          : null,
    };

    return newLineItem;
  };

  // Handle clone single line item
  const handleClone = (lineItemIndex) => {
    const sourceItem = lineItems[lineItemIndex];
    const newLineItem = prepareClonedLineItem(sourceItem);

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: [...(campaign.lineItems || []), newLineItem],
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
    helpers.syncFormValues();

    message.success('Line item cloned successfully');

    setTimeout(() => {
      form.validateFields().catch(() => {});
    }, 100);
  };

  // Handle clone multiple line items
  const handleBulkClone = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one line item to clone');
      return;
    }

    const newLineItems = selectedRowKeys.map((index) => {
      const sourceItem = lineItems[index];
      return prepareClonedLineItem(sourceItem);
    });

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: [...(campaign.lineItems || []), ...newLineItems],
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
    helpers.syncFormValues();

    setSelectedRowKeys([]);
    message.success(`${newLineItems.length} line item${newLineItems.length > 1 ? 's' : ''} cloned successfully`);

    setTimeout(() => {
      form.validateFields().catch(() => {});
    }, 100);
  };

  // Handle bulk delete multiple line items
  const handleBulkDelete = () => {
    const deletableIndices = selectedRowKeys.filter(() => true);

    if (deletableIndices.length === 0) {
      message.warning('No items selected.');
      return;
    }

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: campaign.lineItems.filter((_, i) => !deletableIndices.includes(i)),
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
    helpers.syncFormValues();

    setSelectedRowKeys([]);
    message.success(`${deletableIndices.length} line item${deletableIndices.length > 1 ? 's' : ''} deleted`);

    setTimeout(() => {
      form.validateFields().catch(() => {});
    }, 100);
  };

  // Handle bulk mark for delivery
  const handleBulkMarkForDelivery = () => {
    // Get valid items that can be marked
    const validIndices = selectedRowKeys.filter(index => {
      const validation = validateLineItemForDelivery(lineItems[index], hideCpmFields);
      return validation.isValid;
    });

    if (validIndices.length === 0) {
      message.warning('No valid items to mark. Selected items are missing required fields.');
      return;
    }

    // Check if all valid selected items are already marked - if so, unmark them
    const allMarked = validIndices.every(index => lineItems[index]?.markedForDelivery);
    const newStatus = !allMarked;

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: campaign.lineItems.map((li, i) => {
            if (validIndices.includes(i)) {
              return { ...li, markedForDelivery: newStatus };
            }
            return li;
          }),
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });

    const action = newStatus ? 'marked for delivery' : 'unmarked from delivery';
    message.success(`${validIndices.length} line item${validIndices.length > 1 ? 's' : ''} ${action}`);
    setSelectedRowKeys([]);
  };

  // Handle bulk value add toggle
  const handleBulkValueAdd = () => {
    if (campaignValueAdd) {
      message.warning('Value Add is controlled at the campaign level and cannot be changed individually.');
      return;
    }

    const toggleableIndices = selectedRowKeys;

    if (toggleableIndices.length === 0) {
      message.warning('No items selected.');
      return;
    }

    // Check if all toggleable selected items are already value add - if so, remove it
    const allValueAdd = toggleableIndices.every(index => lineItems[index]?.valueAdd);
    const newStatus = !allValueAdd;

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: campaign.lineItems.map((li, i) => {
            if (toggleableIndices.includes(i)) {
              let updatedName = li.name?.trim() || '';
              if (newStatus) {
                if (!updatedName.endsWith('-$')) {
                  updatedName = updatedName + '-$';
                }
              } else {
                if (updatedName.endsWith('-$')) {
                  updatedName = updatedName.slice(0, -2).trim();
                }
              }
              return { ...li, valueAdd: newStatus, name: updatedName };
            }
            return li;
          }),
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });

    const action = newStatus ? 'marked as Value Add' : 'unmarked from Value Add';
    message.success(`${toggleableIndices.length} line item${toggleableIndices.length > 1 ? 's' : ''} ${action}`);
    setSelectedRowKeys([]);
  };

  // Helper function to create a new line item with defaults
  const createNewLineItem = (existingCount) => {
    const campaign = formValues.campaigns?.[campaignIndex];
    const inheritedValueAdd = campaignValueAdd || false;
    const baseName = `Line Item ${existingCount + 1}`;

    return {
      name: inheritedValueAdd ? `${baseName}-$` : baseName,
      startDate: campaign?.startDate ? moment(campaign.startDate).utc().startOf('day') : moment().utc().startOf('day'),
      endDate: campaign?.endDate ? moment(campaign.endDate).utc().endOf('day') : moment().add(2, 'days').utc().endOf('day'),
      budgetTotal: 0,
      budgetPercentage: 0,
      cpm: campaign?.lineItems?.[0]?.cpm || 7,
      supplySidePlatform: 'Smart AdServer',
      maxBid: 4,
      frequencyCap: 4,
      regionGroups: [],
      targetingGroups: [],
      targetingType: null,
      targetingRegions: [],
      targetingLocations: [],
      targetingCountries: null,
      audienceType: null,
      firstPartyDataAudiences: [],
      adGroup: null,
      valueAdd: inheritedValueAdd,
    };
  };

  // Handle adding new line items
  const handleAddLineItems = (count = 1) => {
    setIsAddingLineItems(true);

    const newLineItems = [];
    const existingCount = lineItems.length;

    for (let i = 0; i < count; i++) {
      newLineItems.push(createNewLineItem(existingCount + i));
    }

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: [...(campaign.lineItems || []), ...newLineItems],
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
    helpers.syncFormValues();

    message.success(`${count} line item${count > 1 ? 's' : ''} created successfully`);
    setIsAddModalVisible(false);
    setAddCount(1);
    setIsAddingLineItems(false);

    setTimeout(() => {
      form.validateFields().catch(() => {});
    }, 100);
  };

  // Handle importing line items from CSV/Excel file
  const handleImportLineItems = (importedLineItems, campaignIdx, options = {}) => {
    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const campaignBudget = currentCampaigns[campaignIndex]?.budget || 0;

    // Normalize percentages: prefer CSV value, fallback to calculated
    let lineItemsWithPercentage = normalizeLineItemPercentages(importedLineItems, campaignBudget);

    // If copyFlightSchedules option is enabled, add flight schedules to each line item
    if (options.copyFlightSchedules && options.campaignFlightSchedules?.length) {
      lineItemsWithPercentage = lineItemsWithPercentage.map(lineItem => ({
        ...lineItem,
        flightSchedule: createFlightSchedulesWithBudget(
          options.campaignFlightSchedules,
          lineItem.budgetTotal,
          lineItem.cpm
        ),
      }));
    }

    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: [...(campaign.lineItems || []), ...lineItemsWithPercentage],
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
    helpers.syncFormValues();

    message.success(`${importedLineItems.length} line item${importedLineItems.length > 1 ? 's' : ''} imported successfully`);
    setIsImportModalVisible(false);

    setTimeout(() => {
      form.validateFields().catch(() => {});
    }, 100);
  };

  // Handle delete line item
  const handleDelete = (lineItemIndex) => {
    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: campaign.lineItems.filter((_, i) => i !== lineItemIndex),
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
    helpers.syncFormValues();
  };

  // Handle mark for delivery
  const handleMarkForDelivery = (lineItemIndex) => {
    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: campaign.lineItems.map((li, i) =>
            i === lineItemIndex ? { ...li, markedForDelivery: !li.markedForDelivery } : li
          ),
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
  };

  // Handle value add toggle
  const handleValueAdd = (lineItemIndex) => {
    if (campaignValueAdd) {
      return; // Controlled by campaign level
    }

    const currentCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        return {
          ...campaign,
          lineItems: campaign.lineItems.map((li, i) => {
            if (i === lineItemIndex) {
              const newValueAdd = !li.valueAdd;
              let updatedName = li.name?.trim() || '';

              if (newValueAdd) {
                if (!updatedName.endsWith('-$')) {
                  updatedName = updatedName + '-$';
                }
              } else {
                if (updatedName.endsWith('-$')) {
                  updatedName = updatedName.slice(0, -2).trim();
                }
              }

              return { ...li, valueAdd: newValueAdd, name: updatedName };
            }
            return li;
          }),
        };
      }
      return campaign;
    });
    form.setFieldsValue({ campaigns: updatedCampaigns });
  };

  // Define table columns
  const columns = [
    {
      title: '',
      key: 'expand',
      width: 48,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={expandedRowKeys.includes(record.index) ? <UpOutlined /> : <DownOutlined />}
          onClick={() => toggleExpand(record.index)}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (value, record) => {
        const handleChange = (e) => {
          const newValue = e.target.value;
          const currentCampaigns = form.getFieldValue('campaigns') || [];
          const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
            if (idx === campaignIndex) {
              return {
                ...campaign,
                lineItems: campaign.lineItems.map((li, i) =>
                  i === record.index ? { ...li, name: newValue } : li
                ),
              };
            }
            return campaign;
          });
          form.setFieldsValue({ campaigns: updatedCampaigns });
        };

        return (
          <Input
            value={value}
            onChange={handleChange}
            placeholder="Line Item Name"
            maxLength={100}
            status={!value ? 'error' : undefined}
          />
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 150,
      render: (value, record) => {
        const safeValue = toDatePickerDisplay(value, timezone);

        const handleChange = (date) => {
          const normalizedDate = date ? datePickerToStartOfDayUtc(date, timezone) : null;
          const currentCampaigns = form.getFieldValue('campaigns') || [];
          const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
            if (idx === campaignIndex) {
              return {
                ...campaign,
                lineItems: campaign.lineItems.map((li, i) =>
                  i === record.index ? { ...li, startDate: normalizedDate } : li
                ),
              };
            }
            return campaign;
          });
          form.setFieldsValue({ campaigns: updatedCampaigns });
          helpers.syncFormValues();
        };

        return (
          <DatePicker
            value={safeValue}
            onChange={handleChange}
            format="MM/DD/YYYY"
            style={{ width: '100%' }}
            status={!value ? 'error' : undefined}
          />
        );
      },
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 150,
      render: (value, record) => {
        const safeValue = toDatePickerDisplay(value, timezone);

        const handleChange = (date) => {
          const normalizedDate = date ? datePickerToEndOfDayUtc(date, timezone) : null;
          const currentCampaigns = form.getFieldValue('campaigns') || [];
          const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
            if (idx === campaignIndex) {
              return {
                ...campaign,
                lineItems: campaign.lineItems.map((li, i) =>
                  i === record.index ? { ...li, endDate: normalizedDate } : li
                ),
              };
            }
            return campaign;
          });
          form.setFieldsValue({ campaigns: updatedCampaigns });
          helpers.syncFormValues();
        };

        return (
          <DatePicker
            value={safeValue}
            onChange={handleChange}
            format="MM/DD/YYYY"
            style={{ width: '100%' }}
            status={!value ? 'error' : undefined}
          />
        );
      },
    },
    {
      title: 'Budget',
      dataIndex: 'budgetTotal',
      key: 'budgetTotal',
      width: 260,
      render: (value, record) => {
        const campaignBudget = formValues.campaigns?.[campaignIndex]?.budget || 0;
        // Always calculate percentage from current campaign budget for consistency
        const percentageValue = campaignBudget > 0 && value > 0
          ? parseFloat(((value / campaignBudget) * 100).toFixed(1))
          : (record.budgetPercentage || 0);

        const handleDollarChange = (newValue) => {
          const currentCampaigns = form.getFieldValue('campaigns') || [];
          const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
            if (idx === campaignIndex) {
              return {
                ...campaign,
                lineItems: campaign.lineItems.map((li, i) => {
                  if (i === record.index) {
                    const newPercentage = campaignBudget > 0
                      ? parseFloat(((newValue / campaignBudget) * 100).toFixed(1))
                      : 0;
                    return { ...li, budgetTotal: newValue, budgetPercentage: newPercentage };
                  }
                  return li;
                }),
              };
            }
            return campaign;
          });
          form.setFieldsValue({ campaigns: updatedCampaigns });
        };

        const handlePercentageChange = (newPercentage) => {
          const budgetTotal = campaignBudget > 0
            ? parseFloat(((newPercentage / 100) * campaignBudget).toFixed(2))
            : 0;

          const currentCampaigns = form.getFieldValue('campaigns') || [];
          const updatedCampaigns = currentCampaigns.map((campaign, idx) => {
            if (idx === campaignIndex) {
              return {
                ...campaign,
                lineItems: campaign.lineItems.map((li, i) =>
                  i === record.index ? { ...li, budgetTotal, budgetPercentage: newPercentage } : li
                ),
              };
            }
            return campaign;
          });
          form.setFieldsValue({ campaigns: updatedCampaigns });
        };

        return (
          <Input.Group compact style={{ display: 'flex' }}>
            <InputNumber
              value={value}
              onChange={handleDollarChange}
              addonBefore="$"
              min={0}
              step={0.01}
              style={{ width: '55%' }}
              status={!value ? 'error' : undefined}
            />
            <InputNumber
              value={percentageValue}
              onChange={handlePercentageChange}
              addonAfter="%"
              min={0}
              max={100}
              step={0.1}
              style={{ width: '45%' }}
            />
          </Input.Group>
        );
      },
    },
    {
      title: 'Impressions',
      key: 'impressions',
      width: 100,
      render: (_, record) => {
        const budget = parseFloat(record.budgetTotal) || 0;
        const cpm = parseFloat(record.cpm) || 0;
        const impressions = cpm > 0 ? Math.round((budget / cpm) * 1000) : 0;
        return <Text>{impressions.toLocaleString()}</Text>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => {
        const validation = validateLineItemForDelivery(record, hideCpmFields);
        const deliveryTooltip = record.markedForDelivery
          ? 'Marked for Delivery'
          : !validation.isValid
            ? `Missing: ${validation.missingFields.join(', ')}`
            : 'Mark for Delivery';

        return (
          <Space size={4}>
            {/* Value Add button */}
            {!record.isOrigin && (
              <Tooltip title={record.valueAdd ? 'Value Add' : 'Mark as Value Add'}>
                <Button
                  icon={<GiftOutlined />}
                  size="small"
                  disabled={campaignValueAdd}
                  style={{
                    backgroundColor: record.valueAdd ? '#722ed1' : 'transparent',
                    borderColor: record.valueAdd ? '#722ed1' : '#d9d9d9',
                    color: record.valueAdd ? '#fff' : '#722ed1',
                  }}
                  onClick={() => handleValueAdd(record.index)}
                />
              </Tooltip>
            )}
            {/* Mark for Delivery button */}
            <Tooltip title={deliveryTooltip}>
              <Button
                icon={<RocketOutlined />}
                size="small"
                disabled={!record.markedForDelivery && !validation.isValid}
                style={{
                  backgroundColor: record.markedForDelivery ? '#52c41a' : 'transparent',
                  borderColor: record.markedForDelivery ? '#52c41a' : '#d9d9d9',
                  color: record.markedForDelivery
                    ? '#fff'
                    : validation.isValid
                      ? '#52c41a'
                      : 'rgba(0, 0, 0, 0.25)',
                }}
                onClick={() => handleMarkForDelivery(record.index)}
              />
            </Tooltip>
            {/* Clone button */}
            <Tooltip title="Clone">
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => handleClone(record.index)}
              />
            </Tooltip>
            {/* Delete button */}
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                disabled={false}
                onClick={() => handleDelete(record.index)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // Expanded row render function
  const expandedRowRender = (record) => {
    const lineItemIndex = record.index;

    // Lazy render: only mount heavy editor when row is actually expanded
    if (!expandedRowKeys.includes(lineItemIndex)) {
      return null;
    }

    // Calculate the flat index for backwards compatibility
    const flatIndex = (formValues.campaigns?.slice(0, campaignIndex) || []).reduce(
      (sum, campaign) => sum + (campaign?.lineItems?.length || 0),
      0
    ) + lineItemIndex;

    return (
      <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
        <CampaignRequestorLineItem
          key={`lineItem-${campaignIndex}-${lineItemIndex}`}
          campaignIndex={campaignIndex}
          formValues={{
            ...formValues,
            campaigns: formValues.campaigns?.[campaignIndex]?.lineItems || [],
            startDate: formValues.campaigns?.[campaignIndex]?.startDate || formValues.startDate,
            endDate: formValues.campaigns?.[campaignIndex]?.endDate || formValues.endDate,
            type: formValues.campaigns?.[campaignIndex]?.type || formValues.type,
            campaignBudget: formValues.campaigns?.[campaignIndex]?.budget || 0,
          }}
          derivedState={derivedState}
          helpers={{
            ...helpers,
            getFieldValue: (path) => {
              if (Array.isArray(path) && path[0] === 'campaigns') {
                return form.getFieldValue(['campaigns', campaignIndex, 'lineItems', lineItemIndex, ...path.slice(2)]);
              }
              return form.getFieldValue(path);
            },
            setFieldsValue: (values) => {
              if (values.campaigns) {
                const updatedLineItems = values.campaigns;
                const currentCampaigns = form.getFieldValue('campaigns') || [];
                const updatedCampaigns = [...currentCampaigns];
                if (updatedCampaigns[campaignIndex]) {
                  updatedCampaigns[campaignIndex] = {
                    ...updatedCampaigns[campaignIndex],
                    lineItems: updatedLineItems,
                  };
                  form.setFieldsValue({ campaigns: updatedCampaigns });
                  helpers.syncFormValues();
                }
              } else {
                form.setFieldsValue(values);
                helpers.syncFormValues();
              }
            },
          }}
          restField={{}}
          name={lineItemIndex}
          fieldKey={lineItemIndex}
          index={lineItemIndex}
          add={addLineItem}
          remove={removeLineItem}
          currentCampaignOrder={currentCampaignOrder}
          isAdvancedMode={isAdvancedMode}
          campaignDsp={campaignDsp}
          campaignValueAdd={campaignValueAdd}
          showOnlyDeliveryItems={false}
          isBulkCloneMode={false}
          isSelected={false}
          onSelectionChange={null}
          hasTraffickerOrProcessorPermission={hasTraffickerOrProcessorPermission}
          isNotDraftStatus={isNotDraftStatus}
          onSaveLineItem={onSaveLineItem}
        />
      </div>
    );
  };

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div style={{ marginTop: 16 }}>
      {/* Toolbar - always visible */}
      <div style={{
        padding: '8px 16px',
        backgroundColor: selectedRowKeys.length > 0 ? '#e6f7ff' : '#fafafa',
        borderRadius: '4px 4px 0 0',
        border: `1px solid ${selectedRowKeys.length > 0 ? '#91d5ff' : '#d9d9d9'}`,
        borderBottom: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8,
      }}>
        {/* Left side: Add actions */}
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={() => handleAddLineItems(1)}
          >
            Add Line Item
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'add-multiple',
                  icon: <PlusOutlined />,
                  label: 'Add Multiple',
                  onClick: () => setIsAddModalVisible(true),
                },
                ...(hasTraffickerOrProcessorPermission ? [{
                  key: 'import',
                  icon: <ImportOutlined />,
                  label: 'Import',
                  onClick: () => setIsImportModalVisible(true),
                }] : []),
                // Sync from Nexxen - only show for NEXXEN DSP campaigns with sync handler
                ...(campaignDsp === 'NEXXEN' && onSyncLineItems ? [
                  { type: 'divider' },
                  {
                    key: 'sync-nexxen',
                    icon: <SyncOutlined spin={isSyncing} />,
                    label: 'Sync from Nexxen',
                    disabled: isSyncing,
                    onClick: onSyncLineItems,
                  },
                ] : []),
                ...(hasTraffickerOrProcessorPermission && isNotDraftStatus && onDeliveryFilterChange ? [
                  { type: 'divider' },
                  {
                    key: 'delivery-only',
                    icon: <FilterOutlined />,
                    label: (
                      <Space>
                        <span>Delivery Only</span>
                        <Switch
                          size="small"
                          checked={showOnlyDeliveryItems}
                          onChange={(checked, e) => {
                            e.stopPropagation();
                            onDeliveryFilterChange(checked);
                          }}
                        />
                      </Space>
                    ),
                  },
                ] : []),
              ],
            }}
            trigger={['click']}
          >
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>

        {/* Right side: Selection actions and View Mode Toggle */}
        <Space size="middle">
          {/* Selection actions (when items selected) */}
          {selectedRowKeys.length > 0 && (
            <Space>
              <Text strong>{selectedRowKeys.length} selected</Text>
              <Tooltip title="Clone Selected">
                <Button type="primary" icon={<CopyOutlined />} size="small" onClick={handleBulkClone} />
              </Tooltip>
              <Popconfirm
                title="Delete selected line items?"
                description={`This will delete ${selectedRowKeys.length} item(s).`}
                onConfirm={handleBulkDelete}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Tooltip title="Delete Selected">
                  <Button danger icon={<DeleteOutlined />} size="small" />
                </Tooltip>
              </Popconfirm>
              {/* Bulk Mark for Delivery - only visible when user has permissions and status is not DRAFT */}
              {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                <Tooltip title={`Mark for Delivery (${selectedRowKeys.filter(i => validateLineItemForDelivery(lineItems[i], hideCpmFields).isValid).length} valid)`}>
                  <Button
                    icon={<RocketOutlined />}
                    size="small"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                    onClick={handleBulkMarkForDelivery}
                    disabled={selectedRowKeys.filter(i => validateLineItemForDelivery(lineItems[i], hideCpmFields).isValid).length === 0}
                  />
                </Tooltip>
              )}
              {/* Bulk Value Add - only visible when user has permissions and status is not DRAFT */}
              {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                <Tooltip title={campaignValueAdd ? 'Value Add controlled at campaign level' : 'Toggle Value Add'}>
                  <Button
                    icon={<GiftOutlined />}
                    size="small"
                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                    onClick={handleBulkValueAdd}
                    disabled={campaignValueAdd || selectedRowKeys.length === 0}
                  />
                </Tooltip>
              )}
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                Clear
              </Button>
            </Space>
          )}

          {/* View Mode Toggle - always visible */}
          {hasTraffickerOrProcessorPermission && onViewModeChange && (
            <Segmented
              size="small"
              value={viewMode}
              onChange={onViewModeChange}
              options={[
                { label: 'Cards', value: 'card', icon: <AppstoreOutlined /> },
                { label: 'Table', value: 'table', icon: <TableOutlined /> },
              ]}
            />
          )}
        </Space>
      </div>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 1200 }}
        rowSelection={rowSelection}
        summary={(pageData) => {
          let totalBudget = 0;
          let totalImpressions = 0;

          pageData.forEach((record) => {
            // Only include line items marked for delivery in totals
            if (!record.markedForDelivery) return;

            const budget = parseFloat(record.budgetTotal) || 0;
            const cpm = parseFloat(record.cpm) || 0;
            totalBudget += budget;
            if (cpm > 0) {
              totalImpressions += Math.round((budget / cpm) * 1000);
            }
          });

          return (
            <Table.Summary>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1} />
                <Table.Summary.Cell index={2}>
                  <Text strong>Total (Delivery)</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5}>
                  <Text strong>
                    ${totalBudget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  <Text strong>{totalImpressions.toLocaleString()}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} />
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [...expandedRowKeys, record.index] : expandedRowKeys.filter(k => k !== record.index));
          },
          expandRowByClick: false,
          showExpandColumn: false,
        }}
        rowClassName={(record) => record.markedForDelivery ? 'line-item-delivery' : ''}
      />

      {/* Modal for adding multiple line items */}
      <Modal
        title="Add Multiple Line Items"
        open={isAddModalVisible}
        onOk={() => handleAddLineItems(addCount)}
        onCancel={() => { setIsAddModalVisible(false); setAddCount(1); }}
        okText={`Add ${addCount} Line Item${addCount > 1 ? 's' : ''}`}
        okButtonProps={{ loading: isAddingLineItems, disabled: isAddingLineItems }}
        cancelButtonProps={{ disabled: isAddingLineItems }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>How many line items would you like to add?</Text>
          <InputNumber
            min={1}
            max={50}
            value={addCount}
            onChange={(value) => setAddCount(value || 1)}
            style={{ width: '100%' }}
          />
        </Space>
      </Modal>

      {/* Modal for importing line items from CSV/Excel */}
      <LineItemImportModal
        visible={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onImport={handleImportLineItems}
        campaignIndex={campaignIndex}
        campaignDefaults={{
          campaignStartDate: formValues.campaigns?.[campaignIndex]?.startDate,
          campaignEndDate: formValues.campaigns?.[campaignIndex]?.endDate,
          defaultCpm: formValues.campaigns?.[campaignIndex]?.cpm || 7,
          inheritedValueAdd: campaignValueAdd || false,
          campaignFlightSchedules: formValues.campaigns?.[campaignIndex]?.flightSchedule || [],
        }}
      />
    </div>
  );
};

export default CampaignRequestorLineItemTable;
